import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Button } from "../../components/ui/button";
import { Link } from "react-router-dom";
import { StatusBadge } from "../../components/ui/status-badge";
import { PerformanceBar } from "../../components/ui/performance-bar";
import { apiRequest } from "../../lib/queryClient";
import { formatDistanceToNow } from "date-fns";
import type { Device } from "../../lib/types";
import { getDevices } from "../../api/axios";

export default function DeviceTable() {
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 5;

  // Sorting state
  const [sortBy, setSortBy] = useState<string>("name");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");

  const { data: devices, isLoading } = useQuery<Device[]>({
    queryKey: ["/api/devices"],
    queryFn: getDevices,
  });

  const handlePing = async (deviceId: string | number | undefined) => {
    if (!deviceId) {
      console.error("Device ID is undefined");
      return;
    }
    try {
      await apiRequest("POST", `/api/devices/${deviceId}/ping`, {});
    } catch (error) {
      console.error("Error pinging device:", error);
    }
  };

  if (isLoading) {
    return (
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg leading-6 font-medium text-neutral-dark">
            Device Status
          </h2>
          <Link
            to="/inventory"
            className="text-sm font-medium text-primary hover:text-primary-dark"
          >
            View all devices
          </Link>
        </div>
        <div className="bg-white shadow rounded-lg border border-gray-200 p-6">
          <div className="animate-pulse space-y-8">
            <div className="h-4 bg-gray-200 rounded w-1/4"></div>
            <div className="space-y-4">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="grid grid-cols-7 gap-4">
                  <div className="h-4 bg-gray-200 rounded col-span-1"></div>
                  <div className="h-4 bg-gray-200 rounded col-span-1"></div>
                  <div className="h-4 bg-gray-200 rounded col-span-1"></div>
                  <div className="h-4 bg-gray-200 rounded col-span-1"></div>
                  <div className="h-4 bg-gray-200 rounded col-span-1"></div>
                  <div className="h-4 bg-gray-200 rounded col-span-1"></div>
                  <div className="h-4 bg-gray-200 rounded col-span-1"></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!devices || devices.length === 0) {
    return (
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg leading-6 font-medium text-neutral-dark">
            Device Status
          </h2>
          <Link
            to="/inventory"
            className="text-sm font-medium text-primary hover:text-primary-dark"
          >
            View all devices
          </Link>
        </div>
        <div className="bg-white shadow rounded-lg border border-gray-200 p-6 text-center text-gray-500">
          No devices found.
        </div>
      </div>
    );
  }

  const deviceList = devices;

  // Sorting logic
  const sortedDevices = [...deviceList].sort((a, b) => {
    let aValue: any = a[sortBy as keyof Device];
    let bValue: any = b[sortBy as keyof Device];
    // Special handling for performance and lastCheck
    if (sortBy === "performance") {
      aValue = a.performance ?? 0;
      bValue = b.performance ?? 0;
    } else if (sortBy === "lastCheck") {
      aValue = new Date(a.lastCheck).getTime();
      bValue = new Date(b.lastCheck).getTime();
    } else {
      aValue = aValue?.toString().toLowerCase?.() ?? "";
      bValue = bValue?.toString().toLowerCase?.() ?? "";
    }
    if (aValue < bValue) return sortDirection === "asc" ? -1 : 1;
    if (aValue > bValue) return sortDirection === "asc" ? 1 : -1;
    return 0;
  });

  // Pagination
  const totalPages = Math.ceil(sortedDevices.length / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const paginatedDevices = sortedDevices.slice(startIndex, startIndex + pageSize);

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg leading-6 font-medium text-neutral-dark">
          Device Status
        </h2>
        <Link
          to="/inventory"
          className="text-sm font-medium text-primary hover:text-primary-dark"
        >
          View all devices
        </Link>
      </div>
      <div className="bg-white shadow overflow-hidden rounded-lg border border-gray-200">
        <div className="flex flex-col">
          <div className="overflow-x-auto">
            <div className="inline-block min-w-full align-middle">
              <div className="overflow-hidden">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th
                        scope="col"
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer select-none"
                        onClick={() => {
                          if (sortBy === "name") setSortDirection(sortDirection === "asc" ? "desc" : "asc");
                          setSortBy("name");
                        }}
                      >
                        Device Name
                      </th>
                      <th
                        scope="col"
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer select-none"
                        onClick={() => {
                          if (sortBy === "ipAddress") setSortDirection(sortDirection === "asc" ? "desc" : "asc");
                          setSortBy("ipAddress");
                        }}
                      >
                        IP Address
                      </th>
                      <th
                        scope="col"
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer select-none"
                        onClick={() => {
                          if (sortBy === "type") setSortDirection(sortDirection === "asc" ? "desc" : "asc");
                          setSortBy("type");
                        }}
                      >
                        Type
                      </th>
                      <th
                        scope="col"
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer select-none"
                        onClick={() => {
                          if (sortBy === "status") setSortDirection(sortDirection === "asc" ? "desc" : "asc");
                          setSortBy("status");
                        }}
                      >
                        Status
                      </th>
                      <th
                        scope="col"
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer select-none"
                        onClick={() => {
                          if (sortBy === "lastCheck") setSortDirection(sortDirection === "asc" ? "desc" : "asc");
                          setSortBy("lastCheck");
                        }}
                      >
                        Last Check
                      </th>
                      <th
                        scope="col"
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer select-none"
                        onClick={() => {
                          if (sortBy === "performance") setSortDirection(sortDirection === "asc" ? "desc" : "asc");
                          setSortBy("performance");
                        }}
                      >
                        Performance
                      </th>
                      <th scope="col" className="relative px-6 py-3">
                        <span className="sr-only">Actions</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {paginatedDevices.map((device) => (
                      <tr key={device.id}>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">
                            {device.name}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-500">{device.ipAddress}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-500">{device.type}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <StatusBadge status={device.status} />
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-500">
                            {formatDistanceToNow(new Date(device.lastCheck), {
                              addSuffix: true,
                            })}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <PerformanceBar value={Math.round(device.performance ?? 0)} />
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handlePing(device.id)}
                          >
                            Ping
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
        <div className="px-6 py-3 bg-gray-50 border-t border-gray-200">
          <div className="flex justify-between">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
            >
              Previous
            </Button>
            <span className="text-sm text-gray-500">
              Page {currentPage} of {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages}
            >
              Next
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
